
import { useState } from 'react';

const pricingPlans = [
  {
    icon: 'ri-briefcase-2-fill',
    label: 'Small',
    sublabel: '小型',
    size: 'リュック・ハンドバッグ',
    sizeEn: 'Backpack / Handbag',
    price: '¥300',
    unit: '/ 個・1日',
    unitEn: 'per item / day',
    color: 'bg-[#d4a574]',
  },
  {
    icon: 'ri-suitcase-2-fill',
    label: 'Medium',
    sublabel: '中型',
    size: 'スーツケース（機内持込サイズ）',
    sizeEn: 'Carry-on Suitcase',
    price: '¥500',
    unit: '/ 個・1日',
    unitEn: 'per item / day',
    color: 'bg-[#c1502e]',
    featured: true,
  },
  {
    icon: 'ri-luggage-cart-fill',
    label: 'Large',
    sublabel: '大型',
    size: 'スーツケース（大型・ゴルフバッグ等）',
    sizeEn: 'Large Suitcase / Golf Bag',
    price: '¥800',
    unit: '/ 個・1日',
    unitEn: 'per item / day',
    color: 'bg-[#8b4513]',
  },
];

const howToSteps = [
  {
    step: '01',
    icon: 'ri-calendar-check-fill',
    title: '事前予約',
    titleEn: 'Book in Advance',
    desc: '予約フォームまたはメールにて、荷物の個数・サイズ・預け入れ日時をご連絡ください。',
  },
  {
    step: '02',
    icon: 'ri-home-2-fill',
    title: 'チェックイン前に来館',
    titleEn: 'Drop Off Before Check-in',
    desc: 'チェックイン（15:00）前でも、事前予約があれば荷物をお預かりします。',
  },
  {
    step: '03',
    icon: 'ri-lock-2-fill',
    title: '安全に保管',
    titleEn: 'Secure Storage',
    desc: '施錠された専用スペースに保管。貴重品は別途ご相談ください。',
  },
  {
    step: '04',
    icon: 'ri-hand-heart-fill',
    title: 'お受け取り',
    titleEn: 'Pick Up Anytime',
    desc: 'チェックアウト後も当日中はお預かり可能。観光を思う存分お楽しみください。',
  },
];

const notes = [
  { icon: 'ri-time-fill', text: '受付時間：9:00〜20:00（時間外は要相談）', en: 'Hours: 9:00–20:00 (outside hours by arrangement)' },
  { icon: 'ri-information-fill', text: '最大お預かり期間：3日間', en: 'Max storage: 3 days' },
  { icon: 'ri-shield-cross-fill', text: '貴重品（現金・パスポート等）はお預かりできません', en: 'Valuables (cash, passports) cannot be stored' },
  { icon: 'ri-user-fill', text: 'ご利用は宿泊者・宿泊予定者に限ります', en: 'Available to guests and future guests only' },
  { icon: 'ri-alert-fill', text: '食品・危険物・生き物はお断りしております', en: 'No food, hazardous materials, or living things' },
  { icon: 'ri-money-yen-circle-fill', text: 'お支払いは現金またはPayPayにて', en: 'Payment: Cash or PayPay' },
];

export default function LuggageStorageSection() {
  const [activeNote, setActiveNote] = useState<number | null>(null);

  return (
    <section id="luggage-storage" className="py-24 bg-gradient-to-b from-[#fff8f0] to-[#f5e6d3] relative overflow-hidden">
      {/* 背景テクスチャ */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }}></div>

      {/* 上部装飾ライン */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#c1502e] to-transparent opacity-40"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* ヘッダー */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-0.5 w-12 bg-gradient-to-r from-transparent to-[#c1502e]" />
            <span className="text-xs tracking-[0.4em] text-[#8b4513] uppercase font-bold px-4 py-1.5 bg-[#fff8f0] border-2 border-[#c1502e] rounded-sm shadow-sm">
              Luggage Storage
            </span>
            <div className="h-0.5 w-12 bg-gradient-to-l from-transparent to-[#c1502e]" />
          </div>

          <h2 className="text-4xl sm:text-5xl font-bold text-[#2c1810] mb-5 tracking-tight" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            荷物保管サービス
            <br />
            <span className="text-[#c1502e]">Luggage Storage Service</span>
          </h2>

          <p className="text-base text-[#5c4033] max-w-2xl mx-auto leading-relaxed">
            チェックイン前・チェックアウト後も安心してお荷物をお預かりします。<br />
            <span className="text-[#8b4513] font-medium">Explore Tokyo hands-free — we'll keep your bags safe.</span>
          </p>
        </div>

        {/* メインビジュアル + 料金表 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">

          {/* 左：画像 */}
          <div className="relative">
            <div className="absolute -inset-2 bg-gradient-to-br from-[#8b4513] to-[#c1502e] rounded-lg opacity-20"></div>
            <div className="relative rounded-lg overflow-hidden border-4 border-[#8b4513] shadow-2xl" style={{
              boxShadow: '0 8px 16px rgba(139, 69, 19, 0.3)'
            }}>
              <img
                src="https://public.readdy.ai/ai/img_res/4cfd9b6ea8bfb1542f00472a715896ce.jpg"
                alt="Luggage Storage at KOMINKA"
                className="w-full h-[380px] object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2c1810]/50 to-transparent"></div>
              <div className="absolute bottom-5 left-5 bg-[#c1502e] text-white px-4 py-2 rounded-sm shadow-lg">
                <p className="text-xs font-bold tracking-widest uppercase">Secure & Safe</p>
                <p className="text-lg font-bold" style={{ fontFamily: "'Noto Serif JP', serif" }}>施錠保管</p>
              </div>
            </div>
          </div>

          {/* 右：料金表 */}
          <div>
            <h3 className="text-2xl font-bold text-[#2c1810] mb-6 text-center" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              料金表 / Pricing
            </h3>
            <div className="space-y-4">
              {pricingPlans.map((plan, i) => (
                <div
                  key={i}
                  className={`relative flex items-center gap-5 rounded-lg p-5 border-2 shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl ${
                    plan.featured
                      ? 'border-[#c1502e] bg-gradient-to-r from-[#fff8f0] to-[#fef3e2]'
                      : 'border-[#d4a574] bg-gradient-to-r from-[#fff8f0] to-[#fef3e2]'
                  }`}
                >
                  {plan.featured && (
                    <div className="absolute -top-3 left-5 bg-[#c1502e] text-white text-xs font-bold px-3 py-0.5 rounded-sm tracking-wider">
                      MOST POPULAR
                    </div>
                  )}
                  <div className={`w-14 h-14 flex items-center justify-center ${plan.color} text-white rounded-lg flex-shrink-0 shadow-md`}>
                    <i className={`${plan.icon} text-2xl`}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2 mb-0.5">
                      <span className="font-bold text-[#2c1810] text-base" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                        {plan.label}
                      </span>
                      <span className="text-xs text-[#8b4513] font-medium">（{plan.sublabel}）</span>
                    </div>
                    <p className="text-sm text-[#5c4033]">{plan.size}</p>
                    <p className="text-xs text-[#8b6f47]">{plan.sizeEn}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-2xl font-bold text-[#c1502e]" style={{ fontFamily: "'Noto Serif JP', serif" }}>{plan.price}</p>
                    <p className="text-xs text-[#8b4513]">{plan.unit}</p>
                    <p className="text-xs text-[#8b6f47]">{plan.unitEn}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* 複数割引バッジ */}
            <div className="mt-5 flex items-center gap-3 bg-gradient-to-r from-[#fef3e2] to-[#fff8f0] border-2 border-dashed border-[#d4a574] rounded-lg p-4">
              <div className="w-10 h-10 flex items-center justify-center bg-[#d4a574] text-white rounded-lg flex-shrink-0">
                <i className="ri-price-tag-3-fill text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-[#2c1810]">3個以上でまとめ割引あり</p>
                <p className="text-xs text-[#5c4033]">Discount available for 3+ items — ask at check-in</p>
              </div>
            </div>
          </div>
        </div>

        {/* ご利用方法 */}
        <div className="mb-20">
          <h3 className="text-center text-2xl font-bold text-[#2c1810] mb-10" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            ご利用方法 / How to Use
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {howToSteps.map((step, i) => (
              <div key={i} className="relative bg-gradient-to-br from-[#fff8f0] to-[#fef3e2] rounded-lg p-6 border-2 border-[#d4a574] shadow-md hover:border-[#c1502e] hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                {/* ステップ番号 */}
                <div className="absolute -top-4 left-5 w-8 h-8 flex items-center justify-center bg-[#c1502e] text-white text-xs font-bold rounded-full shadow-md border-2 border-[#fff8f0]">
                  {step.step}
                </div>
                {/* 矢印（最後以外） */}
                {i < howToSteps.length - 1 && (
                  <div className="hidden lg:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-8 h-8 items-center justify-center text-[#c1502e]">
                    <i className="ri-arrow-right-s-fill text-2xl"></i>
                  </div>
                )}
                <div className="w-12 h-12 flex items-center justify-center bg-[#c1502e] text-white rounded-lg mb-4 shadow-md mt-2">
                  <i className={`${step.icon} text-xl`}></i>
                </div>
                <h4 className="font-bold text-[#2c1810] mb-1" style={{ fontFamily: "'Noto Serif JP', serif" }}>{step.title}</h4>
                <p className="text-xs text-[#8b4513] font-medium mb-2">{step.titleEn}</p>
                <p className="text-sm text-[#5c4033] leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 注意事項 */}
        <div className="bg-gradient-to-br from-[#fef3e2] to-[#fff8f0] rounded-xl border-2 border-[#d4a574] shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-[#8b4513] to-[#c1502e] px-8 py-4 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center text-white">
              <i className="ri-error-warning-fill text-xl"></i>
            </div>
            <h3 className="text-white font-bold text-lg tracking-wide" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              ご利用にあたっての注意事項 / Important Notes
            </h3>
          </div>
          <div className="p-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {notes.map((note, i) => (
              <div
                key={i}
                onMouseEnter={() => setActiveNote(i)}
                onMouseLeave={() => setActiveNote(null)}
                className={`flex items-start gap-3 p-4 rounded-lg border transition-all duration-200 cursor-default ${
                  activeNote === i
                    ? 'border-[#c1502e] bg-white shadow-md'
                    : 'border-[#e8d5b7] bg-white/60'
                }`}
              >
                <div className="w-8 h-8 flex items-center justify-center text-[#c1502e] flex-shrink-0">
                  <i className={`${note.icon} text-lg`}></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-[#2c1810]">{note.text}</p>
                  <p className="text-xs text-[#8b6f47] mt-0.5">{note.en}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTAバナー */}
        <div className="mt-12 relative rounded-xl overflow-hidden shadow-2xl border-2 border-[#8b4513]">
          <img
            src="https://readdy.ai/api/search-image?query=warm%20cozy%20traditional%20Japanese%20tatami%20room%20interior%20showa%20era%201970s%20vintage%20wooden%20furniture%20amber%20light%20nostalgic%20atmosphere%20kominka%20house%20tokyo%20magome%20quiet%20peaceful&width=1200&height=220&seq=luggage-cta-bg-001&orientation=landscape"
            alt="Luggage Storage CTA"
            className="w-full h-[200px] object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2c1810]/88 via-[#2c1810]/72 to-[#2c1810]/50"></div>
          <div className="absolute inset-0 flex flex-col sm:flex-row items-center justify-between px-8 sm:px-12 gap-6">
            <div>
              <p className="text-[#d4a574] text-xs font-bold tracking-widest uppercase mb-2">Available for All Guests</p>
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-1" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                手ぶらで東京観光を
              </h3>
              <p className="text-[#fef3e2]/80 text-sm">
                荷物はKOMINKAに預けて、身軽に昭和の街を歩きましょう。
              </p>
            </div>
            <a
              href="#reservation"
              className="flex items-center gap-3 px-8 py-4 bg-[#c1502e] hover:bg-[#8b4513] text-white font-bold rounded-sm transition-all hover:scale-105 shadow-lg border-2 border-[#8b4513] whitespace-nowrap"
              style={{ boxShadow: '0 4px 0 #8b4513' }}
            >
              <i className="ri-luggage-cart-fill text-xl"></i>
              荷物保管を予約する
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
